import React, {
  useCallback,
  useEffect,
  useState,
  type ReactElement,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAPI, PostApiReponsePayload, SAMLApiResponsePayload } from '~/src/hooks/useAPI';
import { trackEvent } from '~/src/helpers/trackEvent';
import {
  browserSupportsWebAuthnAutofill,
  loginWithPasskeyDiscoverable,
  loginWithPasskeyConditional,
  PasskeyDismissedError,
} from '~/src/helpers/passkey';

import { sendPostEventToEmailSubscriptionApplication } from '~/src/helpers/PushwooshService/pushwooshService';
import { getCaptchaVersion } from '~/src/components/Recaptcha';
import { useEnvironment } from '~/src/contexts/environment';
import { useLang } from '~/src/polyglot';
import { LoginPage } from './LoginPage.view';

import type { LoginSubmitData, ChekSSOData } from './LoginPage.types';

export function LoginPageWithSSO(): ReactElement {
  const { callAPI } = useAPI();
  const { productName } = useEnvironment();
  const lang = useLang();
  const [searchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState('');
  const [isSSOChecked, setIsSSOChecked] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [isLoadedInviteEmail, setIsLoadedInviteEmail] = useState(true);
  const inviteCode = searchParams.get('invite') || '';

  const fetchData = useCallback(() => {
    if (inviteCode == null) {
      return;
    }

    const payload = new URLSearchParams();
    setIsLoadedInviteEmail(false);
    payload.append('code', inviteCode);

    fetch(
      '/api/validate-invite',
      {
        method: 'POST',
        body: payload,
      },
    )
      .then((res) => {
        if (res.status === 200) {
          res.json()
            .then(({ email }) => {
              setInviteEmail(email);
            });
        }
      })
      .catch((error) => {
        setErrorMessage(error.message);
      })
      .finally(() => setIsLoadedInviteEmail(true));
  }, [inviteCode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Arm conditional-UI passkey login on mount. If the browser supports it,
  // simplewebauthn silently asks for a passkey through navigator.credentials
  // with mediation: 'conditional'; the prompt surfaces only when the user
  // focuses the username field (which has autocomplete="username webauthn").
  // The promise resolves only if the user actually picks a passkey — no
  // pick, no resolve, no complaint. On resolve we follow the redirect.
  useEffect(() => {
    let cancelled = false;
    browserSupportsWebAuthnAutofill().then((supported) => {
      if (!supported || cancelled) {
        return;
      }
      loginWithPasskeyConditional()
        .then((url) => {
          if (cancelled) return;
          trackEvent({
            name: 'LOGIN',
            properties: { method: 'passkey-conditional' },
          });
          document.location.href = url;
        })
        .catch((err) => {
          // Silently swallow — the user either didn't pick a passkey, the
          // browser hid the prompt, or the backend was unreachable. The
          // normal login form is still right there.
          if (!cancelled) {
            // eslint-disable-next-line no-console
            console.debug('passkey conditional auth declined', err);
          }
        });
    });
    return (): void => {
      cancelled = true;
    };
  }, []);

  const loginHandler = useCallback(({
    password,
    username,
    token,
  }: LoginSubmitData): Promise<void> => {
    setErrorMessage('');

    return callAPI(
      'api/login',
      {
        password,
        username,
        'g-recaptcha-response': token,
        'g-recaptcha-version': getCaptchaVersion(productName),
        ...(inviteCode && { invite: inviteCode }),
      },
    )
      .then((response) => {
        const { redirect, message } = response as PostApiReponsePayload;

        trackEvent({
          name: 'LOGIN',
          properties: {
            email: username,
          },
        });

        sendPostEventToEmailSubscriptionApplication(username, 'AccountLogin', {});

        // awaiting redirect and ok response will be errored as unexpected behavior
        return redirect ? redirect() : setErrorMessage(message);
      })
      .catch((error) => setErrorMessage(error.message));
  }, [callAPI, inviteCode, productName]);

  const passkeyLoginHandler = useCallback(async (): Promise<void> => {
    setErrorMessage('');
    try {
      // Discoverable flow: no email up front, the browser shows its own
      // account picker and the backend resolves the user from the
      // credential ID. We don't know the email until the redirect lands,
      // so the LOGIN event carries the method but not the address.
      const url = await loginWithPasskeyDiscoverable();
      trackEvent({
        name: 'LOGIN',
        properties: { method: 'passkey' },
      });
      // Top-level navigation, so /authorize can hop to app.* without CORS.
      document.location.href = url;
    } catch (err) {
      // User-initiated dismissal of the WebAuthn prompt is not a failure —
      // just reset and let them try again or fall back to password.
      if (err instanceof PasskeyDismissedError) {
        return;
      }
      // Every other failure (no credential for this user, network, backend
      // 4xx/5xx, finish step error) collapses into one neutral message.
      // Avoids leaking whether the account exists / has a passkey and
      // doesn't falsely imply the user needs to set one up.
      setErrorMessage(lang({ message: 'LOGIN.ERROR.PASSKEY_FAILED' }));
    }
  }, [lang]);

  const checkForSSOHandler = useCallback(({
    username,
    token,
  }: ChekSSOData): Promise<void> => {
    setErrorMessage('');

    return callAPI(
      'api/get-saml-endpoint',
      {
        username,
        'g-recaptcha-response': token,
        'g-recaptcha-version': getCaptchaVersion(productName),
      },
      'GET',
    )
      .then((response) => {
        const { ssoUrl } = response as SAMLApiResponsePayload;

        ssoUrl ? document.location.href = ssoUrl : setIsSSOChecked(true);
      })
      .catch((error) => setErrorMessage(error));
  }, [callAPI, productName]);

  return (
    <LoginPage
      inviteCode={inviteCode}
      inviteEmail={inviteEmail}
      isLoadedInviteEmail={isLoadedInviteEmail}
      loginHandler={loginHandler}
      checkForSSOHandler={checkForSSOHandler}
      passkeyLoginHandler={passkeyLoginHandler}
      isSSOChecked={isSSOChecked}
      errorMessage={errorMessage}
    />
  );
}
